import { Fragment, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion, AnimatePresence } from "motion/react";
import {
  AlertCircle,
  ArrowLeft,
  ArrowRight,
  Bot,
  Calendar,
  Check,
  CheckCircle2,
  ChevronDown,
  Clock,
  Copy,
  Download,
  ExternalLink,
  Eye,
  FileText,
  Filter,
  Layers,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  Menu,
  Phone,
  Play,
  Plus,
  Radio,
  RefreshCw,
  Search,
  Send,
  Share2,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  Users,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavStore } from "../../components/layout/nav-store";
import { Badge } from "../../components/ui/badge";
import { CopyButton } from "../../components/ui/copy-button";
import { cn } from "../../lib/utils";
import { campaignsApi, templatesApi, type WhatsAppTemplate } from "./api";
import { WhatsAppMessageBubble } from "./templates-page";
import { listContactBatches } from "../workflows/api";
import { chatbotApi } from "../chatbot/chatbot-api";
import { Dropdown } from "../../components/ui/dropdown";
import { WhatsAppCampaignScheduler } from "./campaign-scheduler";

const primary =
  "inline-flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-brand to-amber-500 px-3.5 py-2 text-xs font-semibold text-white shadow-sm shadow-brand/20 transition hover:from-brand-hover hover:to-amber-600 active:scale-[0.99] disabled:opacity-50";
const secondary =
  "inline-flex items-center gap-1.5 rounded-lg border border-line bg-paper px-3 py-2 text-xs font-semibold text-ink shadow-2xs transition hover:bg-soft disabled:opacity-50 dark:bg-[#1f1c1a]";

const STATUS_CONFIG: Record<
  string,
  { label: string; badgeClass: string; icon: any; dotClass: string }
> = {
  sending: {
    label: "Sending",
    badgeClass:
      "border-brand/30 bg-brand-soft/80 text-brand dark:bg-brand/15 dark:text-brand",
    dotClass: "bg-brand animate-ping",
    icon: RefreshCw,
  },
  scheduled: {
    label: "Scheduled",
    badgeClass:
      "border-amber-500/30 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400",
    dotClass: "bg-amber-500",
    icon: Clock,
  },
  completed: {
    label: "Completed",
    badgeClass:
      "border-emerald-500/30 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
    dotClass: "bg-emerald-500",
    icon: CheckCircle2,
  },
  failed: {
    label: "Failed",
    badgeClass:
      "border-red-500/30 bg-danger-soft text-danger dark:bg-red-950/40 dark:text-red-400",
    dotClass: "bg-danger",
    icon: XCircle,
  },
  queued: {
    label: "Queued",
    badgeClass: "border-line bg-soft text-ink-3 dark:bg-white/5",
    dotClass: "bg-ink-3",
    icon: Clock,
  },
};

function getCounts(c: any) {
  const total = c.total_recipients ?? 0;
  const sent = c.sent_count ?? 0;
  const failed = c.failed_count ?? 0;
  const queued = c.queued_count ?? 0;
  const pending = Math.max(total - sent - failed - queued, 0);
  const percent = total > 0 ? Math.min(100, Math.round(((sent + failed) / total) * 100)) : 0;
  return { total, sent, failed, queued, pending, percent };
}

const AVATAR_PALETTES = [
  "bg-gradient-to-br from-brand/15 to-amber-500/10 text-brand border-brand/25 dark:bg-brand/20 dark:text-brand",
  "bg-gradient-to-br from-blue-500/15 to-indigo-500/10 text-blue-600 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-900/60",
  "bg-gradient-to-br from-purple-500/15 to-fuchsia-500/10 text-purple-600 border-purple-200 dark:bg-purple-950/40 dark:text-purple-300 dark:border-purple-900/60",
  "bg-gradient-to-br from-emerald-500/15 to-teal-500/10 text-emerald-600 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-900/60",
  "bg-gradient-to-br from-rose-500/15 to-pink-500/10 text-rose-600 border-rose-200 dark:bg-rose-950/40 dark:text-rose-300 dark:border-rose-900/60",
  "bg-gradient-to-br from-amber-500/15 to-orange-500/10 text-amber-700 border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900/60",
  "bg-gradient-to-br from-cyan-500/15 to-sky-500/10 text-cyan-700 border-cyan-200 dark:bg-cyan-950/40 dark:text-cyan-300 dark:border-cyan-900/60",
  "bg-gradient-to-br from-indigo-500/15 to-violet-500/10 text-indigo-600 border-indigo-200 dark:bg-indigo-950/40 dark:text-indigo-300 dark:border-indigo-900/60",
];

function getCampaignInitial(name?: string, id?: string): string {
  const source = (name || id || "Campaign").trim();
  const cleaned = source.replace(/^[^a-zA-Z0-9]+/, "");
  return (cleaned.charAt(0) || source.charAt(0) || "C").toUpperCase();
}

function getCampaignAvatarPalette(nameOrId: string): string {
  let hash = 0;
  for (let i = 0; i < nameOrId.length; i++) {
    hash = nameOrId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
}

function formatCampaignDate(c: any): string {
  if (c.mode === "later" && c.scheduled_at) {
    try {
      const d = new Date(c.scheduled_at);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return c.scheduled_at;
    }
  }
  if (c.created_at) {
    try {
      const d = new Date(c.created_at);
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Immediate";
    }
  }
  return "Immediate";
}

// ── In-Line Template & Campaign Settings Preview Drawer ──
function CampaignPreviewContent({
  group,
  activeRunId,
  templateMap,
  batchNameMap,
  instancePhoneMap,
  agentNameMap,
}: {
  group: any;
  activeRunId: string | null;
  templateMap: Map<string, WhatsAppTemplate>;
  batchNameMap: Map<string, { name: string; count: number }>;
  instancePhoneMap: Map<string, string>;
  agentNameMap: Map<string, string>;
}) {
  const currentRun =
    group.runs.find(
      (r: any) => r.campaign_id === (activeRunId || group.latestRun.campaign_id),
    ) || group.latestRun;
  const counts = getCounts(currentRun);
  const template = group.template_id ? templateMap.get(group.template_id) : undefined;
  const isTemplate = group.message_kind === "template";
  const agentName = agentNameMap.get(group.agent_id) || group.agent_id;
  const batchInfo = batchNameMap.get(currentRun.batch_id);
  const batchLabel = batchInfo
    ? `${batchInfo.name} (${batchInfo.count} contacts)`
    : currentRun.batch_id
      ? `Batch ${currentRun.batch_id.slice(-8)}`
      : "All Contacts";
  const senderLabel =
    instancePhoneMap.get(currentRun.instance_name) ||
    (currentRun.instance_name ? currentRun.instance_name.slice(0, 16) : "Default Sender");

  return (
    <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-12">
      {/* 1. Template / Agent Preview */}
      <div className="rounded-xl border border-line/60 bg-paper p-3.5 shadow-2xs dark:bg-[#1a1716] lg:col-span-5 space-y-2.5">
        <div className="flex items-center justify-between border-b border-line/50 pb-2">
          <div className="flex items-center gap-2">
            {isTemplate ? (
              <FileText className="h-4 w-4 text-brand" />
            ) : (
              <Bot className="h-4 w-4 text-purple-600" />
            )}
            <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink">
              {isTemplate ? "Message Template Preview" : "AI Agent Opener Preview"}
            </span>
          </div>
          {template && (
            <span className="rounded bg-soft px-2 py-0.5 font-mono text-[10px] font-semibold uppercase text-ink-3">
              {template.category || "Marketing"} · {template.language || "en"}
            </span>
          )}
        </div>

        {isTemplate ? (
          template ? (
            <div className="mx-auto max-w-[340px] overflow-hidden rounded-xl border border-line/60 bg-[#efeae2]/85 p-2.5 dark:bg-[#0c1317]">
              <WhatsAppMessageBubble template={template} compact />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-line p-8 text-center text-xs text-ink-3">
              <FileText className="mb-1.5 h-7 w-7 text-ink-3/40" />
              <p className="font-semibold text-ink">Template: {group.template_id || "Untitled"}</p>
              <p className="mt-0.5 text-[11px] text-ink-3">Template preview not available.</p>
            </div>
          )
        ) : (
          <div className="rounded-xl border border-purple-200/60 bg-purple-50/50 p-4 text-xs dark:border-purple-900/40 dark:bg-purple-950/20">
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-purple-600 text-xs font-bold text-white">
                <Bot className="h-4 w-4" />
              </div>
              <div>
                <p className="font-bold text-ink">{agentName || "AI Voice & Chat Agent"}</p>
                <p className="text-[10px] text-ink-3">Interactive conversational responder</p>
              </div>
            </div>
            <p className="text-[11px] leading-relaxed text-ink-2">
              Dispatched with an intelligent AI conversation opener. The agent dynamically handles incoming recipient questions and responses.
            </p>
          </div>
        )}
      </div>

      {/* 2. Campaign Settings & Dispatch Telemetry */}
      <div className="rounded-xl border border-line/60 bg-paper p-3.5 shadow-2xs dark:bg-[#1a1716] lg:col-span-7 space-y-3">
        <div className="flex items-center justify-between border-b border-line/50 pb-2">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="h-4 w-4 text-brand" />
            <span className="font-mono text-[11px] font-bold uppercase tracking-wider text-ink">
              Campaign Settings &amp; Dispatch Details
            </span>
          </div>
          <span className="font-mono text-[10px] font-semibold text-brand">
            ID: {currentRun.campaign_id}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="rounded-lg border border-line/50 bg-soft/40 p-2.5">
            <span className="block font-mono text-[10px] uppercase text-ink-3">Sender WhatsApp Number</span>
            <span className="mt-1 block truncate font-mono font-bold text-ink">{senderLabel}</span>
          </div>

          <div className="rounded-lg border border-line/50 bg-soft/40 p-2.5">
            <span className="block font-mono text-[10px] uppercase text-ink-3">Contact Batch</span>
            <span className="mt-1 block truncate font-semibold text-ink" title={batchLabel}>
              {batchLabel}
            </span>
          </div>

          <div className="rounded-lg border border-line/50 bg-soft/40 p-2.5">
            <span className="block font-mono text-[10px] uppercase text-ink-3">Dispatch Rate (Concurrency)</span>
            <span className="mt-1 block font-mono font-bold text-ink">{currentRun.concurrency || group.concurrency} msg/s</span>
          </div>

          <div className="rounded-lg border border-line/50 bg-soft/40 p-2.5">
            <span className="block font-mono text-[10px] uppercase text-ink-3">Launch / Schedule Time</span>
            <span className="mt-1 block truncate font-semibold text-ink">{formatCampaignDate(currentRun)}</span>
          </div>
        </div>

        {/* Telemetry Summary Progress Strip */}
        <div className="space-y-2 rounded-lg border border-line/60 bg-soft/30 p-3">
          <div className="flex items-center justify-between">
            <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-ink-3">
              Delivery Telemetry
            </span>
            <span className="font-mono text-xs font-bold text-ink">
              {counts.percent}% Delivered
            </span>
          </div>

          <div className="h-2 w-full overflow-hidden rounded-full bg-line/60">
            <div
              className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-400 transition-all"
              style={{ width: `${counts.percent}%` }}
            />
          </div>

          <div className="grid grid-cols-4 gap-2 pt-1 text-center font-mono text-[11px]">
            <div className="rounded border border-line/50 bg-paper p-1.5 shadow-2xs dark:bg-[#201d1b]">
              <span className="block text-[9px] uppercase text-ink-3">Total</span>
              <span className="font-bold text-ink">{counts.total}</span>
            </div>
            <div className="rounded border border-emerald-500/20 bg-emerald-50/60 p-1.5 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300">
              <span className="block text-[9px] uppercase">Sent</span>
              <span className="font-bold">{counts.sent}</span>
            </div>
            <div className="rounded border border-danger/20 bg-danger-soft/60 p-1.5 text-danger">
              <span className="block text-[9px] uppercase">Failed</span>
              <span className="font-bold">{counts.failed}</span>
            </div>
            <div className="rounded border border-line/40 bg-soft p-1.5 text-ink-3">
              <span className="block text-[9px] uppercase">Queued</span>
              <span className="font-bold">{counts.queued}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main Campaigns Page Component ──
export function CampaignsPage() {
  const { token } = useAuth() as any;
  const [searchParams, setSearchParams] = useSearchParams();
  const openMobileNav = useNavStore((s) => s.openMobileNav);

  const [view, setView] = useState<"list" | "create">(
    searchParams.get("batchId") || searchParams.get("action") === "new"
      ? "create"
      : "list",
  );

  const [viewMode, setViewMode] = useState<"table" | "cards">("table");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [kindFilter, setKindFilter] = useState<string>("all");
  const [openCampaignName, setOpenCampaignName] = useState<string | null>(null);
  const [activeRunId, setActiveRunId] = useState<string | null>(null);
  const [selectedPreset, setSelectedPreset] = useState<any | null>(null);

  // Core Campaigns Query
  const campaignsQuery = useQuery({
    queryKey: ["whatsapp-campaigns", token],
    queryFn: campaignsApi.list,
    enabled: Boolean(token),
    refetchInterval: 15000,
  });

  // Supporting queries for human-readable resolution of IDs
  const templatesQuery = useQuery({
    queryKey: ["whatsapp-templates", token],
    queryFn: templatesApi.list,
    enabled: Boolean(token),
  });
  const batchesQuery = useQuery({
    queryKey: ["contact-batches"],
    queryFn: listContactBatches,
    enabled: Boolean(token),
  });
  const instancesQuery = useQuery({
    queryKey: ["chatbot-instances", token],
    queryFn: () => chatbotApi.instances(token),
    enabled: Boolean(token),
  });
  const agentsQuery = useQuery({
    queryKey: ["chatbot-agents", token],
    queryFn: () => chatbotApi.agents(token),
    enabled: Boolean(token),
  });

  // Lookup maps
  const fullTemplateMap = useMemo(() => {
    const map = new Map<string, WhatsAppTemplate>();
    (templatesQuery.data ?? []).forEach((t: any) => {
      if (t.template_id) map.set(t.template_id, t);
    });
    return map;
  }, [templatesQuery.data]);

  const templateNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (templatesQuery.data ?? []).forEach((t: any) => {
      if (t.template_id) map.set(t.template_id, t.name);
    });
    return map;
  }, [templatesQuery.data]);

  const batchNameMap = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    (batchesQuery.data ?? []).forEach((b: any) => {
      if (b.batch_id) map.set(b.batch_id, { name: b.name, count: b.valid_contacts ?? 0 });
    });
    return map;
  }, [batchesQuery.data]);

  const instancePhoneMap = useMemo(() => {
    const map = new Map<string, string>();
    (instancesQuery.data ?? []).forEach((inst: any) => {
      if (inst.instance_name) {
        map.set(
          inst.instance_name,
          inst.phone_number ? `+${inst.phone_number}` : inst.instance_name,
        );
      }
    });
    return map;
  }, [instancesQuery.data]);

  const agentNameMap = useMemo(() => {
    const map = new Map<string, string>();
    (agentsQuery.data ?? []).forEach((a: any) => {
      if (a.id) map.set(a.id, a.name || a.id);
    });
    return map;
  }, [agentsQuery.data]);

  const allRows = campaignsQuery.data ?? [];

  // Group campaigns by campaign name to support re-using campaign settings
  interface CampaignGroup {
    name: string;
    initial: string;
    avatarPalette: string;
    runs: any[];
    runsCount: number;
    latestRun: any;
    message_kind: "template" | "ai_agent";
    template_id: string;
    agent_id: string;
    instance_name: string;
    concurrency: number;
    totalRecipients: number;
    totalSent: number;
    totalFailed: number;
    totalQueued: number;
    overallPercent: number;
    latestStatus: string;
    latestTime: string;
    hasActiveSending: boolean;
  }

  const campaignGroups = useMemo<CampaignGroup[]>(() => {
    const rawList = campaignsQuery.data ?? [];
    const groupsMap = new Map<string, any[]>();

    for (const c of rawList) {
      const name = (c.campaign || c.campaign_id || "Untitled Campaign").trim();
      const existing = groupsMap.get(name) || [];
      existing.push(c);
      groupsMap.set(name, existing);
    }

    const groups: CampaignGroup[] = [];
    for (const [name, runs] of groupsMap.entries()) {
      runs.sort((a, b) => {
        const timeA = new Date(a.created_at || a.scheduled_at || 0).getTime();
        const timeB = new Date(b.created_at || b.scheduled_at || 0).getTime();
        return timeB - timeA;
      });

      const latestRun = runs[0];
      const initial = getCampaignInitial(name, latestRun.campaign_id);
      const avatarPalette = getCampaignAvatarPalette(name);

      let totalRecipients = 0;
      let totalSent = 0;
      let totalFailed = 0;
      let totalQueued = 0;
      let hasActiveSending = false;

      for (const r of runs) {
        const k = getCounts(r);
        totalRecipients += k.total;
        totalSent += k.sent;
        totalFailed += k.failed;
        totalQueued += k.queued;
        if (String(r.status).toLowerCase() === "sending") {
          hasActiveSending = true;
        }
      }

      const overallPercent =
        totalRecipients > 0
          ? Math.min(100, Math.round(((totalSent + totalFailed) / totalRecipients) * 100))
          : 0;

      const latestStatus = hasActiveSending ? "sending" : String(latestRun.status || "queued").toLowerCase();
      const latestTime = formatCampaignDate(latestRun);

      groups.push({
        name,
        initial,
        avatarPalette,
        runs,
        runsCount: runs.length,
        latestRun,
        message_kind: latestRun.message_kind || "template",
        template_id: latestRun.template_id || "",
        agent_id: latestRun.agent_id || "",
        instance_name: latestRun.instance_name || "",
        concurrency: latestRun.concurrency || 20,
        totalRecipients,
        totalSent,
        totalFailed,
        totalQueued,
        overallPercent,
        latestStatus,
        latestTime,
        hasActiveSending,
      });
    }

    groups.sort((a, b) => {
      const timeA = new Date(a.latestRun.created_at || a.latestRun.scheduled_at || 0).getTime();
      const timeB = new Date(b.latestRun.created_at || b.latestRun.scheduled_at || 0).getTime();
      return timeB - timeA;
    });

    return groups;
  }, [campaignsQuery.data]);

  // Filtering on grouped campaigns
  const filteredGroups = useMemo(() => {
    let list = [...campaignGroups];
    const q = search.trim().toLowerCase();

    if (q) {
      list = list.filter((g) => {
        const tName = templateNameMap.get(g.template_id) || "";
        const aName = agentNameMap.get(g.agent_id) || "";
        const phone = instancePhoneMap.get(g.instance_name) || "";
        const matchesRuns = g.runs.some((r) => {
          const bName = batchNameMap.get(r.batch_id)?.name || "";
          return (r.campaign_id || "").toLowerCase().includes(q) || bName.toLowerCase().includes(q);
        });
        return (
          g.name.toLowerCase().includes(q) ||
          tName.toLowerCase().includes(q) ||
          aName.toLowerCase().includes(q) ||
          phone.toLowerCase().includes(q) ||
          matchesRuns
        );
      });
    }

    if (statusFilter !== "all") {
      list = list.filter((g) => g.latestStatus === statusFilter);
    }

    if (kindFilter !== "all") {
      list = list.filter((g) => g.message_kind === kindFilter);
    }

    return list;
  }, [campaignGroups, search, statusFilter, kindFilter, templateNameMap, agentNameMap, instancePhoneMap, batchNameMap]);

  const toggleOpenCampaign = (name: string, defaultRunId: string) => {
    if (openCampaignName === name) {
      setOpenCampaignName(null);
      setActiveRunId(null);
    } else {
      setOpenCampaignName(name);
      setActiveRunId(defaultRunId);
    }
  };

  const handleRunAgain = (g: CampaignGroup) => {
    setSelectedPreset({
      campaign: g.name,
      messageKind: g.message_kind,
      templateId: g.template_id,
      agentId: g.agent_id,
      instanceName: g.instance_name,
      concurrency: g.concurrency,
    });
    setView("create");
  };

  // Aggregate metrics
  const totalCampaignsCount = campaignGroups.length;
  const totalRunsAll = allRows.length;
  const sendingGroupsCount = campaignGroups.filter((g) => g.latestStatus === "sending").length;
  const scheduledGroupsCount = campaignGroups.filter((g) => g.latestStatus === "scheduled").length;
  const completedGroupsCount = campaignGroups.filter((g) => g.latestStatus === "completed").length;
  const totalSentAll = allRows.reduce((acc, c) => acc + (c.sent_count ?? 0), 0);
  const totalRecipientsAll = allRows.reduce((acc, c) => acc + (c.total_recipients ?? 0), 0);
  const overallSuccessRate = totalRecipientsAll > 0
    ? Math.round((totalSentAll / totalRecipientsAll) * 100)
    : 100;

  return (
    <div className="space-y-4 px-4 pb-12 pt-3 sm:px-5 sm:pt-4 md:px-6">
      {/* ── VIEW 1: CAMPAIGNS HUB (LIST & TABLE) ── */}
      {view === "list" && (
        <motion.div
          key="campaign-list"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          className="space-y-4"
        >
          {/* Header Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={openMobileNav}
                aria-label="Open navigation menu"
                className="flex h-8 w-8 items-center justify-center rounded-lg text-ink-3 transition-colors hover:bg-soft hover:text-ink md:hidden"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="flex items-center gap-2">
                <h1 className="text-sm font-semibold text-ink">Campaign</h1>
                <Badge tone="neutral">{totalCampaignsCount} campaigns · {totalRunsAll} runs</Badge>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* View Switcher: Table vs Cards */}
              <div className="flex items-center rounded-lg border border-line bg-paper p-0.5 shadow-2xs dark:bg-[#1f1c1a]">
                <button
                  type="button"
                  onClick={() => setViewMode("table")}
                  aria-label="Table View"
                  title="Enterprise Table View"
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-md transition",
                    viewMode === "table"
                      ? "bg-brand text-white shadow-2xs"
                      : "text-ink-3 hover:text-ink hover:bg-soft",
                  )}
                >
                  <ListIcon className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode("cards")}
                  aria-label="Cards View"
                  title="Cards Gallery View"
                  className={cn(
                    "flex h-7 w-7 items-center justify-center rounded-md transition",
                    viewMode === "cards"
                      ? "bg-brand text-white shadow-2xs"
                      : "text-ink-3 hover:text-ink hover:bg-soft",
                  )}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Refresh Button */}
              <button
                type="button"
                aria-label="Refresh campaigns"
                onClick={() => {
                  void campaignsQuery.refetch();
                }}
                className="flex h-8 w-8 items-center justify-center rounded-lg border border-line bg-paper text-ink-3 transition hover:bg-soft hover:text-ink dark:bg-[#1f1c1a]"
                title="Refresh campaigns"
              >
                <RefreshCw
                  className={cn("h-3.5 w-3.5", campaignsQuery.isFetching && "animate-spin")}
                />
              </button>

              {/* Primary Add Campaign Button */}
              <button
                type="button"
                className={primary}
                onClick={() => {
                  setSelectedPreset(null);
                  setView("create");
                }}
                aria-label="Add Campaign"
              >
                <Plus className="h-4 w-4" />
                <span>Add Campaign</span>
              </button>
            </div>
          </div>

          {/* Executive Telemetry Ribbon (Stunning First Impression) */}
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-5">
            <button
              type="button"
              onClick={() => {
                setStatusFilter("all");
                setKindFilter("all");
              }}
              className={cn(
                "group relative overflow-hidden rounded-xl border p-3.5 text-left transition-all duration-300",
                statusFilter === "all" && kindFilter === "all"
                  ? "border-brand bg-gradient-to-br from-brand/10 to-brand/5 ring-1 ring-brand/40 shadow-sm"
                  : "border-line/70 bg-paper-card hover:border-line hover:shadow-card dark:bg-[#292524]",
              )}
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                  All Campaigns
                </span>
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-soft text-ink-3 group-hover:text-brand transition-colors">
                  <Send className="h-3 w-3" />
                </div>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-mono text-xl font-bold text-ink">
                  {totalCampaignsCount}
                </span>
                <span className="text-[10px] text-ink-3 font-mono">({totalRunsAll} runs)</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("sending")}
              className={cn(
                "group relative overflow-hidden rounded-xl border p-3.5 text-left transition-all duration-300",
                statusFilter === "sending"
                  ? "border-brand bg-gradient-to-br from-brand/15 to-brand/5 ring-1 ring-brand/40 shadow-sm"
                  : "border-line/70 bg-paper-card hover:border-line hover:shadow-card dark:bg-[#292524]",
              )}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-brand">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand animate-ping" />
                  Sending Now
                </span>
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-brand-soft text-brand">
                  <Zap className="h-3 w-3" />
                </div>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-mono text-xl font-bold text-brand">
                  {sendingGroupsCount}
                </span>
                <span className="text-[10px] text-ink-3 font-mono">active pipeline</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("scheduled")}
              className={cn(
                "group relative overflow-hidden rounded-xl border p-3.5 text-left transition-all duration-300",
                statusFilter === "scheduled"
                  ? "border-amber-500 bg-amber-50/70 ring-1 ring-amber-500/40 shadow-sm dark:bg-amber-950/30"
                  : "border-line/70 bg-paper-card hover:border-line hover:shadow-card dark:bg-[#292524]",
              )}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                  <Clock className="h-3 w-3" />
                  Scheduled
                </span>
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
                  <Calendar className="h-3 w-3" />
                </div>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-mono text-xl font-bold text-ink">
                  {scheduledGroupsCount}
                </span>
                <span className="text-[10px] text-ink-3 font-mono">pending queue</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("completed")}
              className={cn(
                "group relative overflow-hidden rounded-xl border p-3.5 text-left transition-all duration-300",
                statusFilter === "completed"
                  ? "border-emerald-500 bg-emerald-50/70 ring-1 ring-emerald-500/40 shadow-sm dark:bg-emerald-950/30"
                  : "border-line/70 bg-paper-card hover:border-line hover:shadow-card dark:bg-[#292524]",
              )}
            >
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                  <CheckCircle2 className="h-3 w-3" />
                  Completed
                </span>
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                  <Check className="h-3 w-3" />
                </div>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-mono text-xl font-bold text-emerald-600 dark:text-emerald-400">
                  {completedGroupsCount}
                </span>
                <span className="text-[10px] text-ink-3 font-mono">completed</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => setStatusFilter("all")}
              className="hidden lg:flex flex-col justify-between rounded-xl border border-line/70 bg-paper-card p-3.5 text-left transition-all hover:border-line dark:bg-[#292524]"
            >
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-ink-3">
                  Avg. Success Rate
                </span>
                <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400">
                  <TrendingUp className="h-3 w-3" />
                </div>
              </div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className="font-mono text-xl font-bold text-ink">
                  {overallSuccessRate}%
                </span>
                <span className="text-[10px] text-ink-3 font-mono">
                  {totalSentAll} delivered
                </span>
              </div>
            </button>
          </div>

          {/* Unified Filter & Search Bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-line/60 bg-paper-card p-3 shadow-card dark:bg-[#292524] dark:shadow-card-dark">
            <div className="relative min-w-[200px] flex-1 sm:max-w-md">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3" />
              <input
                type="text"
                placeholder="Search campaigns, templates, batches, sender numbers... ( / )"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 w-full rounded-lg border border-line bg-paper pl-8 pr-7 text-xs text-ink outline-none transition placeholder:text-ink-3 focus:border-brand dark:bg-[#1f1c1a]"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch("")}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink"
                >
                  <X className="h-3 w-3" />
                </button>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {/* Status Segmented Tabs */}
              <div className="flex items-center rounded-lg border border-line bg-soft/50 p-0.5 text-xs">
                {["all", "sending", "scheduled", "completed", "failed"].map((st) => (
                  <button
                    key={st}
                    type="button"
                    onClick={() => setStatusFilter(st)}
                    className={cn(
                      "rounded-md px-2.5 py-1 font-medium capitalize transition text-[11px]",
                      statusFilter === st
                        ? "bg-paper-card text-ink shadow-2xs dark:bg-[#292524]"
                        : "text-ink-3 hover:text-ink",
                    )}
                  >
                    {st}
                  </button>
                ))}
              </div>

              {/* Message Type Selector */}
              <div className="w-40 shrink-0">
                <Dropdown
                  ariaLabel="Message Type filter"
                  size="sm"
                  value={kindFilter}
                  onChange={setKindFilter}
                  options={[
                    { value: "all", label: "All Message Types" },
                    { value: "template", label: "Meta Template" },
                    { value: "ai_agent", label: "AI Agent" },
                  ]}
                />
              </div>
            </div>
          </div>

          {/* Loading & Empty States */}
          {campaignsQuery.isLoading && (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-brand" />
              <p className="mt-3 text-sm text-ink-3">Loading campaigns…</p>
            </div>
          )}

          {!campaignsQuery.isLoading && filteredGroups.length === 0 && (
            <div className="flex flex-col items-center justify-center rounded-2xl border border-line/60 bg-paper-card py-16 px-4 text-center shadow-card dark:bg-[#292524]">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-soft text-brand shadow-xs">
                <Send className="h-6 w-6" />
              </div>
              <p className="mt-3 text-sm font-semibold text-ink">No campaigns found</p>
              <p className="mt-1 text-xs text-ink-3 max-w-sm">
                Launch your first bulk WhatsApp campaign to contact batches using Meta-approved templates or interactive AI agents.
              </p>
              <button
                type="button"
                className={`${primary} mt-4`}
                onClick={() => {
                  setSelectedPreset(null);
                  setView("create");
                }}
              >
                <Plus className="h-4 w-4" />
                <span>Add Campaign</span>
              </button>
            </div>
          )}

          {/* ── PRESENTATION 1: ENTERPRISE DATA TABLE (REUSABLE CAMPAIGNS HUB) ── */}
          {!campaignsQuery.isLoading && viewMode === "table" && filteredGroups.length > 0 && (
            <div className="overflow-x-auto rounded-2xl border border-line/60 bg-paper-card shadow-card dark:bg-[#292524] dark:shadow-card-dark">
              <table className="w-full min-w-[780px] table-fixed text-left text-xs">
                <thead className="sticky top-0 z-10 border-b border-line bg-stone-100 dark:bg-stone-800/80 font-mono text-[10px] uppercase tracking-wider text-ink-2 dark:text-stone-300">
                  <tr>
                    <th className="py-3 px-3.5 w-[21%]">Campaign</th>
                    <th className="py-3 px-2.5 w-[15%]">Message Target</th>
                    <th className="py-3 px-2.5 w-[14%]">Batch</th>
                    <th className="py-3 px-2.5 w-[13%]">Sender</th>
                    <th className="py-3 px-2.5 w-[12%]">Progress</th>
                    <th className="py-3 px-2.5 w-[11%]">Last Run</th>
                    <th className="py-3 px-2.5 w-[9%]">Status</th>
                    <th className="py-3 px-3.5 w-[5%] text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-line/60">
                  {filteredGroups.map((g) => {
                    const isTemplate = g.message_kind === "template";
                    const statusKey = g.latestStatus;
                    const statusCfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.queued;

                    const templateName = templateNameMap.get(g.template_id) || g.template_id || "Untitled";
                    const agentName = agentNameMap.get(g.agent_id) || g.agent_id;
                    const latestBatchInfo = batchNameMap.get(g.latestRun.batch_id);
                    const latestBatchLabel = latestBatchInfo
                      ? `${latestBatchInfo.name} (${latestBatchInfo.count})`
                      : (g.latestRun.batch_id ? `Batch ${g.latestRun.batch_id.slice(-8)}` : "All Contacts");
                    const senderLabel = instancePhoneMap.get(g.instance_name) || (g.instance_name ? g.instance_name.slice(0, 16) : "Default Sender");

                    const isOpen = openCampaignName === g.name;

                    return (
                      <Fragment key={g.name}>
                        <tr
                          onClick={() => toggleOpenCampaign(g.name, g.latestRun.campaign_id)}
                          className={cn(
                            "transition-colors hover:bg-stone-50 dark:hover:bg-stone-800/50 cursor-pointer",
                            isOpen && "bg-brand-soft/10",
                          )}
                        >
                          {/* Campaign Name + Avatar + Runs count badge */}
                          <td className="py-3 px-3.5 font-semibold text-ink">
                            <div className="flex items-center gap-2 min-w-0">
                              <div
                                className={cn(
                                  "flex h-7 w-7 shrink-0 items-center justify-center rounded-lg border shadow-2xs font-bold text-xs select-none",
                                  g.avatarPalette,
                                )}
                              >
                                {g.initial}
                              </div>
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5">
                                  <span className="truncate font-sans font-bold text-xs text-ink" title={g.name}>
                                    {g.name}
                                  </span>
                                  <span className="shrink-0 inline-flex items-center rounded bg-stone-100 dark:bg-stone-800 px-1 py-0.2 font-mono text-[9px] font-semibold text-ink-2 border border-line/40">
                                    {g.runsCount} {g.runsCount === 1 ? "run" : "runs"}
                                  </span>
                                </div>
                                <span className="font-mono text-[9px] text-ink-3 truncate block">
                                  ID: {g.latestRun.campaign_id.slice(0, 10)}…
                                </span>
                              </div>
                            </div>
                          </td>

                          {/* Message Target */}
                          <td className="py-3 px-2.5">
                            <span className="inline-flex items-center gap-1.5 rounded-md bg-soft px-2 py-0.5 text-[11px] font-semibold text-ink border border-line/40 truncate max-w-full">
                              {isTemplate ? (
                                <>
                                  <FileText className="h-3 w-3 text-brand shrink-0" />
                                  <span className="truncate" title={templateName}>{templateName}</span>
                                </>
                              ) : (
                                <>
                                  <Bot className="h-3 w-3 text-purple-600 shrink-0" />
                                  <span className="truncate" title={agentName}>{agentName}</span>
                                </>
                              )}
                            </span>
                          </td>

                          {/* Audience Batch (Latest) */}
                          <td className="py-3 px-2.5">
                            <span className="inline-flex items-center gap-1 text-[11px] text-ink-2 font-medium truncate max-w-full" title={latestBatchLabel}>
                              <Users className="h-3 w-3 text-ink-3 shrink-0" />
                              <span className="truncate">{latestBatchLabel}</span>
                            </span>
                          </td>

                          {/* Sender Instance */}
                          <td className="py-3 px-2.5 font-mono text-[11px] text-ink-2">
                            <span className="inline-flex items-center gap-1 rounded bg-paper px-1.5 py-0.5 border border-line/60 shadow-2xs truncate max-w-full dark:bg-[#1a1716]" title={senderLabel}>
                              <Phone className="h-2.5 w-2.5 text-brand shrink-0" />
                              <span className="truncate font-semibold">{senderLabel}</span>
                            </span>
                          </td>

                          {/* Delivery Progress Bar */}
                          <td className="py-3 px-2.5">
                            <div className="flex flex-col gap-1 w-full max-w-[110px]">
                              <div className="flex items-center justify-between text-[10px] font-mono leading-none">
                                <span className="font-bold text-ink">
                                  {g.totalSent}/{g.totalRecipients}
                                </span>
                                <span className={cn("font-bold", g.overallPercent === 100 ? "text-emerald-600 dark:text-emerald-400" : "text-brand")}>
                                  {g.overallPercent}%
                                </span>
                              </div>
                              <div className="h-1.5 w-full rounded-full bg-soft overflow-hidden">
                                <div
                                  className={cn(
                                    "h-full transition-all duration-300 rounded-full",
                                    statusKey === "completed"
                                      ? "bg-gradient-to-r from-emerald-500 to-teal-400"
                                      : statusKey === "failed"
                                        ? "bg-danger"
                                        : "bg-gradient-to-r from-brand to-amber-500",
                                  )}
                                  style={{ width: `${g.overallPercent}%` }}
                                />
                              </div>
                            </div>
                          </td>

                          {/* Time */}
                          <td className="py-3 px-2.5 text-[11px] text-ink-3 truncate whitespace-nowrap">
                            <span title={g.latestTime}>{g.latestTime}</span>
                          </td>

                          {/* Status Badge */}
                          <td className="py-3 px-2.5">
                            <span
                              className={cn(
                                "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase whitespace-nowrap",
                                statusCfg.badgeClass,
                              )}
                            >
                              <span className={cn("h-1 w-1 rounded-full", statusCfg.dotClass)} />
                              {statusCfg.label}
                            </span>
                          </td>

                          {/* Actions: Expand Toggle only (Run Again removed) */}
                          <td className="py-3 px-3.5 text-right" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center justify-end gap-1.5 whitespace-nowrap">
                              <button
                                type="button"
                                onClick={() => toggleOpenCampaign(g.name, g.latestRun.campaign_id)}
                                aria-label={isOpen ? "Collapse campaign" : "Expand campaign"}
                                className={cn(
                                  "flex h-6.5 w-6.5 items-center justify-center rounded-md border transition shrink-0",
                                  isOpen
                                    ? "border-brand bg-brand-soft text-brand dark:bg-brand/20"
                                    : "border-line bg-paper text-ink-3 hover:bg-soft hover:text-ink dark:bg-[#1f1c1a]",
                                )}
                              >
                                <ChevronDown
                                  className={cn(
                                    "h-3.5 w-3.5 transition-transform duration-200",
                                    isOpen && "rotate-180",
                                  )}
                                />
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* REUSABLE CAMPAIGN RUNS & RECIPIENTS ROW EXPANDS DIRECTLY BELOW THIS ENTRY */}
                        {isOpen && (
                          <tr className="bg-stone-50/70 dark:bg-stone-900/40 animate-in fade-in duration-150">
                            <td colSpan={8} className="p-0 border-b border-line/60">
                              <div className="p-3 sm:p-4 space-y-3">
                                {/* Sleek Configuration Banner */}
                                <div className="flex flex-wrap items-center justify-between gap-2.5 rounded-xl border border-line/70 bg-paper p-2.5 shadow-2xs dark:bg-[#201d1b]">
                                  <div className="flex flex-wrap items-center gap-2 text-xs min-w-0">
                                    <span
                                      className={cn(
                                        "flex h-6 w-6 shrink-0 items-center justify-center rounded-md border text-[11px] font-bold select-none",
                                        g.avatarPalette,
                                      )}
                                    >
                                      {g.initial}
                                    </span>
                                    <div className="flex items-center gap-1.5">
                                      <span className="font-bold text-xs text-ink truncate max-w-[150px]">{g.name}</span>
                                      <span className="rounded bg-soft px-1.5 py-0.2 font-mono text-[9px] font-semibold text-ink-3">
                                        {g.runsCount} {g.runsCount === 1 ? "run" : "runs"}
                                      </span>
                                    </div>
                                    <span className="text-line-2 dark:text-stone-700">|</span>
                                    <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-ink-2">
                                      <span className="inline-flex items-center gap-1 rounded bg-soft px-2 py-0.5">
                                        <span className="text-ink-3 font-normal">Target:</span>
                                        <strong className="text-ink font-semibold truncate max-w-[120px]">{isTemplate ? templateName : agentName}</strong>
                                      </span>
                                      <span className="inline-flex items-center gap-1 rounded bg-soft px-2 py-0.5">
                                        <span className="text-ink-3 font-normal">Sender:</span>
                                        <strong className="text-ink font-semibold truncate max-w-[110px]">{senderLabel}</strong>
                                      </span>
                                      <span className="inline-flex items-center gap-1 rounded bg-soft px-2 py-0.5">
                                        <span className="text-ink-3 font-normal">Rate:</span>
                                        <strong className="text-ink font-semibold">{g.concurrency} msg/s</strong>
                                      </span>
                                    </div>
                                  </div>

                                  <div className="flex items-center gap-1.5 shrink-0">
                                    <button
                                      type="button"
                                      onClick={() => handleRunAgain(g)}
                                      className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-brand to-amber-500 hover:from-brand-hover hover:to-amber-600 px-3 py-1.5 text-xs font-semibold text-white shadow-xs transition whitespace-nowrap active:scale-[0.98]"
                                    >
                                      <Play className="h-3 w-3 fill-current" />
                                      <span>Schedule New Run</span>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setOpenCampaignName(null);
                                        setActiveRunId(null);
                                      }}
                                      className="flex h-6.5 w-6.5 items-center justify-center rounded-md text-ink-3 hover:bg-soft hover:text-ink transition"
                                      aria-label="Close campaign details"
                                    >
                                      <X className="h-3.5 w-3.5" />
                                    </button>
                                  </div>
                                </div>

                                {/* Multi-Run Selector (if multiple runs exist) */}
                                {g.runs.length > 1 && (
                                  <div className="flex flex-wrap items-center gap-1.5 px-0.5">
                                    <span className="font-mono text-[10px] font-bold uppercase tracking-wider text-ink-3 mr-1">
                                      Run History:
                                    </span>
                                    {g.runs.map((r, idx) => {
                                      const isSelected = (activeRunId || g.latestRun.campaign_id) === r.campaign_id;
                                      const rCounts = getCounts(r);
                                      return (
                                        <button
                                          key={r.campaign_id}
                                          type="button"
                                          onClick={() => setActiveRunId(r.campaign_id)}
                                          className={cn(
                                            "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs transition",
                                            isSelected
                                              ? "border-brand bg-brand-soft/80 text-brand font-semibold shadow-2xs dark:bg-brand/20"
                                              : "border-line bg-paper text-ink-3 hover:bg-soft hover:text-ink dark:bg-[#1f1c1a]",
                                          )}
                                        >
                                          <span className="font-mono text-[11px] font-bold">
                                            Run #{g.runs.length - idx} {idx === 0 ? "(Latest)" : ""}
                                          </span>
                                          <span className="text-[10px] opacity-75">· {formatCampaignDate(r)}</span>
                                          <span className="font-mono text-[10px] font-bold text-ink">
                                            {rCounts.percent}%
                                          </span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}

                                {/* Template & Settings Preview Drawer */}
                                <CampaignPreviewContent
                                  group={g}
                                  activeRunId={activeRunId}
                                  templateMap={fullTemplateMap}
                                  batchNameMap={batchNameMap}
                                  instancePhoneMap={instancePhoneMap}
                                  agentNameMap={agentNameMap}
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* ── PRESENTATION 2: COMPACT CARDS VIEW ── */}
          {!campaignsQuery.isLoading && viewMode === "cards" && filteredGroups.length > 0 && (
            <div className="grid grid-cols-1 gap-4.5 md:grid-cols-2 lg:grid-cols-3">
              {filteredGroups.map((g) => {
                const isTemplate = g.message_kind === "template";
                const statusKey = g.latestStatus;
                const statusCfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.queued;

                const templateName = templateNameMap.get(g.template_id) || g.template_id || "Untitled";
                const agentName = agentNameMap.get(g.agent_id) || g.agent_id;
                const latestBatchInfo = batchNameMap.get(g.latestRun.batch_id);
                const latestBatchLabel = latestBatchInfo
                  ? `${latestBatchInfo.name} (${latestBatchInfo.count})`
                  : (g.latestRun.batch_id ? `Batch ${g.latestRun.batch_id.slice(-8)}` : "All Contacts");
                const senderLabel = instancePhoneMap.get(g.instance_name) || (g.instance_name ? g.instance_name.slice(0, 16) : "Default Sender");

                const isOpen = openCampaignName === g.name;

                return (
                  <article
                    key={g.name}
                    className="flex flex-col justify-between rounded-2xl border border-line/70 bg-paper-card p-4 shadow-card hover:border-brand/40 hover:shadow-card-hover transition-all duration-200 dark:bg-[#292524] dark:shadow-card-dark"
                  >
                    <div>
                      {/* Card Top: Icon + Name + Runs + Status */}
                      <div className="flex items-start justify-between gap-2.5">
                        <div className="flex items-start gap-2.5 min-w-0">
                          <div
                            className={cn(
                              "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border shadow-2xs font-bold text-sm select-none",
                              g.avatarPalette,
                            )}
                          >
                            {g.initial}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-1.5">
                              <h2 className="truncate text-sm font-bold text-ink" title={g.name}>
                                {g.name}
                              </h2>
                              <span className="rounded bg-stone-100 dark:bg-stone-800 px-1.5 py-0.2 font-mono text-[9px] font-semibold text-ink-2">
                                {g.runsCount} {g.runsCount === 1 ? "run" : "runs"}
                              </span>
                            </div>
                            <p className="truncate text-xs text-ink-3">
                              {isTemplate ? `Template: ${templateName}` : `Agent: ${agentName}`}
                            </p>
                          </div>
                        </div>

                        <span
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 font-mono text-[9px] font-bold uppercase shrink-0",
                            statusCfg.badgeClass,
                          )}
                        >
                          <span className={cn("h-1.5 w-1.5 rounded-full", statusCfg.dotClass)} />
                          {statusCfg.label}
                        </span>
                      </div>

                      {/* Meta Chips */}
                      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-ink-3">
                        <span className="flex items-center gap-1 rounded bg-soft px-2 py-0.5" title="Latest Audience Batch">
                          <Users className="h-3 w-3" />
                          <span className="truncate max-w-[130px]">{latestBatchLabel}</span>
                        </span>
                        <span className="flex items-center gap-1 rounded bg-soft px-2 py-0.5" title="Sender Number">
                          <Phone className="h-3 w-3" />
                          <span className="truncate max-w-[110px]">{senderLabel}</span>
                        </span>
                      </div>

                      {/* Delivery Progress Bar */}
                      <div className="mt-4 space-y-1.5 rounded-xl border border-line/60 bg-soft/40 p-3">
                        <div className="flex items-center justify-between text-xs font-mono">
                          <span className="font-semibold text-ink">
                            {g.totalSent} of {g.totalRecipients} delivered
                          </span>
                          <span className="font-bold text-brand">{g.overallPercent}%</span>
                        </div>
                        <div className="h-1.5 w-full rounded-full bg-line/60 overflow-hidden">
                          <div
                            className={cn(
                              "h-full transition-all duration-300 rounded-full",
                              statusKey === "completed"
                                ? "bg-gradient-to-r from-emerald-500 to-teal-400"
                                : statusKey === "failed"
                                  ? "bg-danger"
                                  : "bg-brand",
                            )}
                            style={{ width: `${g.overallPercent}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-ink-3 pt-0.5">
                          <span>{g.totalFailed > 0 ? `${g.totalFailed} failed` : "0 failed"}</span>
                          <span>{g.latestTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Bottom Actions: Re-use & Schedule + Runs Toggle */}
                    <div className="mt-4 border-t border-line/50 pt-3 flex flex-col gap-2">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleOpenCampaign(g.name, g.latestRun.campaign_id)}
                          className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-line bg-paper px-3 py-1.5 text-xs font-semibold text-ink transition hover:bg-soft dark:bg-[#1f1c1a]"
                        >
                          <span>{isOpen ? "Hide" : `Runs (${g.runsCount})`}</span>
                          <ChevronDown
                            className={cn("h-3.5 w-3.5 transition-transform", isOpen && "rotate-180")}
                          />
                        </button>
                      </div>

                      {isOpen && (
                        <div className="mt-2 space-y-3 border-t border-line/60 pt-3">
                          {g.runs.length > 1 && (
                            <div className="flex flex-wrap gap-1.5">
                              {g.runs.map((r, idx) => {
                                const isSel = (activeRunId || g.latestRun.campaign_id) === r.campaign_id;
                                return (
                                  <button
                                    key={r.campaign_id}
                                    type="button"
                                    onClick={() => setActiveRunId(r.campaign_id)}
                                    className={cn(
                                      "rounded-md px-2 py-0.5 text-[10px] font-mono font-medium transition",
                                      isSel
                                        ? "bg-brand text-white font-bold"
                                        : "bg-soft text-ink-3 hover:text-ink",
                                    )}
                                  >
                                    Run #{g.runs.length - idx} {idx === 0 ? "(Latest)" : ""}
                                  </button>
                                );
                              })}
                            </div>
                          )}
                          <CampaignPreviewContent
                            group={g}
                            activeRunId={activeRunId}
                            templateMap={fullTemplateMap}
                            batchNameMap={batchNameMap}
                            instancePhoneMap={instancePhoneMap}
                            agentNameMap={agentNameMap}
                          />
                        </div>
                      )}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </motion.div>
      )}

      {/* ── VIEW 2: SCHEDULE / ADD CAMPAIGN ── */}
      {view === "create" && (
        <motion.div
          key="campaign-create"
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -4 }}
          className="mx-auto w-full max-w-4xl space-y-5"
        >
          {/* Header Bar */}
          <div className="flex items-center justify-between border-b border-line/60 pb-3">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => {
                  setView("list");
                  if (searchParams.get("batchId") || searchParams.get("action")) {
                    setSearchParams({});
                  }
                }}
                className="flex items-center gap-1.5 rounded-lg border border-line bg-paper px-3 py-1.5 text-xs font-semibold text-ink shadow-2xs hover:bg-soft transition dark:bg-[#1f1c1a]"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                <span>Back to Campaigns</span>
              </button>
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-ink">Schedule Campaign</h2>
                <Badge tone="brand">New Campaign</Badge>
              </div>
            </div>
          </div>

          {/* Embedded Scheduler Component */}
          <WhatsAppCampaignScheduler
            initialPreset={selectedPreset}
            onCancel={() => {
              setView("list");
              if (searchParams.get("batchId") || searchParams.get("action")) {
                setSearchParams({});
              }
            }}
            onComplete={() => {
              setView("list");
              if (searchParams.get("batchId") || searchParams.get("action")) {
                setSearchParams({});
              }
              void campaignsQuery.refetch();
            }}
          />
        </motion.div>
      )}
    </div>
  );
}
